import { useCallback, useEffect, useRef, useState } from "react"

import {
  currencyFractionDigits,
  validateOverageSettingsDraft,
} from "../utils/overageSettings"

/** Presentation values supplied by a released-contract adapter; never a transport schema. */
export interface PaidOverageReview {
  scopeKey: string
  reviewKey: string
  disclosureVersion: string
  disclosureText: string
  priceLines: readonly string[]
  collectionTiming: string
  validityLabel: string
  currency: string
  limitedMaximumMinor: number | null
  unlimitedOffered: boolean
  paymentStatus: "ready" | "setup" | "authentication"
}

interface ReviewIntent {
  readonly attemptId: string
  readonly reviewKey: string
  readonly surface: "signup" | "quick_enablement"
  readonly decision: "accept" | "decline"
  readonly budget:
    | { readonly mode: "limited"; readonly minor: number }
    | { readonly mode: "unlimited" }
    | null
  readonly recoveryAttemptId: string | null
}

interface ReviewResult {
  scopeKey: string
  attemptId: string
  result: "accepted" | "declined" | "payment-setup" | "payment-authentication"
  reference: string
}

export interface PaidOverageReviewPort {
  load: (signal: AbortSignal) => Promise<PaidOverageReview | null>
  /** Return only independently verified receipts binding the complete intent, offer, actor and organization. */
  decide: (intent: ReviewIntent, signal: AbortSignal) => Promise<ReviewResult>
  /** Read the retained original receipt without writing; current offers or missing evidence cannot resolve an outcome. */
  recheck: (
    intent: ReviewIntent,
    signal: AbortSignal,
  ) => Promise<ReviewResult | null>
  classify: (error: unknown) => "conflict" | "uncertain" | "rejected"
}

class UnverifiedDecisionOutcome extends Error {}

type Phase =
  | "loading"
  | "unavailable"
  | "review"
  | "saving"
  | "load-error"
  | "conflict"
  | "uncertain"
  | "unverified"
  | "checking-decision"
  | "rejected"
  | ReviewResult["result"]

export function usePaidOverageReview(
  scopeKey: string,
  surface: "signup" | "quick_enablement",
  port: PaidOverageReviewPort,
) {
  const [review, setReview] = useState<PaidOverageReview | null>(null)
  const [phase, setPhase] = useState<Phase>("loading")
  const [mode, setMode] = useState<"limited" | "unlimited">("limited")
  const [amount, setAmount] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const pending = useRef<ReviewIntent | null>(null)
  const recovery = useRef<string | null>(null)
  const [reference, setReference] = useState<string | null>(null)
  const controller = useRef<AbortController | null>(null)
  const generation = useRef(0)
  const busy = useRef(false)
  const checkingFrom = useRef<"uncertain" | "unverified">("unverified")

  const load = useCallback(async () => {
    if (busy.current || pending.current !== null) {
      return
    }
    busy.current = true
    const version = generation.current
    const next = new AbortController()
    controller.current = next
    setPhase("loading")
    try {
      const value = await port.load(next.signal)
      if (next.signal.aborted || generation.current !== version) {
        return
      }
      if (value !== null && value.scopeKey !== scopeKey) {
        throw new Error("Organization changed")
      }
      if (value !== null) {
        if (
          value.limitedMaximumMinor !== null &&
          (!Number.isSafeInteger(value.limitedMaximumMinor) ||
            value.limitedMaximumMinor < 0)
        ) {
          throw new Error("Approved budget maximum could not be verified")
        }
        if (value.limitedMaximumMinor === null && !value.unlimitedOffered) {
          throw new Error("No approved budget choice is available")
        }
        setMode((current) => {
          const offered =
            current === "limited"
              ? value.limitedMaximumMinor !== null
              : value.unlimitedOffered
          return offered
            ? current
            : value.limitedMaximumMinor !== null
              ? "limited"
              : "unlimited"
        })
      }
      setReview(value)
      setConfirmed(false)
      setPhase(
        value === null
          ? "unavailable"
          : recovery.current !== null && value.paymentStatus !== "ready"
            ? value.paymentStatus === "setup"
              ? "payment-setup"
              : "payment-authentication"
            : "review",
      )
    } catch {
      if (!next.signal.aborted && generation.current === version) {
        setPhase("load-error")
      }
    } finally {
      if (generation.current === version) {
        busy.current = false
      }
    }
  }, [port, scopeKey])

  useEffect(() => {
    if (pending.current !== null) {
      setPhase((current) =>
        current === "checking-decision"
          ? checkingFrom.current
          : current === "unverified"
            ? "unverified"
            : "uncertain",
      )
    } else {
      void load()
    }
    return () => {
      generation.current += 1
      controller.current?.abort()
      busy.current = false
    }
  }, [load])

  const parsed =
    review === null
      ? null
      : validateOverageSettingsDraft(
          { overagesEnabled: true, limitState: mode, amount },
          review.currency,
        )
  const budgetError =
    review !== null && currencyFractionDigits(review.currency) === null
      ? "This currency cannot be safely represented. Contact Coval before accepting."
      : (parsed?.amountError ??
        (mode === "limited" &&
        review !== null &&
        (review.limitedMaximumMinor === null ||
          (parsed?.limitMinor ?? 0) > review.limitedMaximumMinor)
          ? "Choose a budget within the approved maximum."
          : mode === "unlimited" && !review?.unlimitedOffered
            ? "Unlimited spending is not offered."
            : null))
  const issues = [
    ...(budgetError ? [budgetError] : []),
    ...(!confirmed ? ["Accept the overage terms to continue."] : []),
  ]
  const locked = phase !== "review"
  const canDecline =
    phase === "review" ||
    phase === "payment-setup" ||
    phase === "payment-authentication"

  function applyResult(intent: ReviewIntent, result: ReviewResult) {
    if (
      result?.scopeKey !== scopeKey ||
      result.attemptId !== intent.attemptId ||
      typeof result.reference !== "string" ||
      !result.reference.trim() ||
      ![
        "accepted",
        "declined",
        "payment-setup",
        "payment-authentication",
      ].includes(result.result) ||
      (intent.decision === "decline") !== (result.result === "declined")
    ) {
      throw new UnverifiedDecisionOutcome(
        "Decision outcome could not be verified",
      )
    }
    setReference(result.reference)
    setPhase(result.result)
    recovery.current = result.result.startsWith("payment-")
      ? intent.attemptId
      : null
    pending.current = null
  }

  async function decide(decision: "accept" | "decline", retry = false) {
    if (
      busy.current ||
      review === null ||
      (!retry && (decision === "decline" ? !canDecline : locked)) ||
      (retry && (phase !== "uncertain" || pending.current === null))
    ) {
      return
    }
    setAttempted(decision === "accept")
    if (!retry && decision === "accept" && issues.length > 0) {
      return
    }
    const intent: ReviewIntent =
      retry && pending.current !== null
        ? pending.current
        : Object.freeze({
            attemptId: crypto.randomUUID(),
            reviewKey: review.reviewKey,
            surface,
            decision,
            budget:
              decision === "decline"
                ? null
                : mode === "limited"
                  ? Object.freeze({ mode, minor: parsed?.limitMinor ?? 0 })
                  : Object.freeze({ mode }),
            recoveryAttemptId: recovery.current,
          })
    const version = generation.current
    const next = new AbortController()
    controller.current = next
    busy.current = true
    pending.current = intent
    setPhase("saving")
    try {
      const result = await port.decide(intent, next.signal)
      if (next.signal.aborted || generation.current !== version) {
        return
      }
      applyResult(intent, result)
    } catch (error: unknown) {
      if (!next.signal.aborted && generation.current === version) {
        const failure =
          error instanceof UnverifiedDecisionOutcome
            ? "unverified"
            : retry
              ? "uncertain"
              : port.classify(error)
        if (failure === "conflict" || failure === "rejected") {
          pending.current = null
        }
        setPhase(failure)
      }
    } finally {
      if (generation.current === version) {
        busy.current = false
      }
    }
  }

  async function recheck() {
    const intent = pending.current
    if (
      busy.current ||
      intent === null ||
      (phase !== "uncertain" && phase !== "unverified")
    ) {
      return
    }
    const version = generation.current
    const next = new AbortController()
    controller.current = next
    busy.current = true
    checkingFrom.current = phase
    setPhase("checking-decision")
    try {
      const result = await port.recheck(intent, next.signal)
      if (next.signal.aborted || generation.current !== version) {
        return
      }
      if (result === null) {
        throw new Error("The original receipt is unavailable")
      }
      applyResult(intent, result)
    } catch (error: unknown) {
      if (!next.signal.aborted && generation.current === version) {
        setPhase(
          error instanceof UnverifiedDecisionOutcome ? "unverified" : phase,
        )
      }
    } finally {
      if (generation.current === version) {
        busy.current = false
      }
    }
  }

  return {
    review,
    phase,
    mode,
    amount,
    confirmed,
    attempted,
    reference,
    issues,
    budgetError,
    locked,
    canDecline,
    setMode: (value: typeof mode) => {
      setMode(value)
      setConfirmed(false)
    },
    setAmount: (value: string) => {
      setAmount(value)
      setConfirmed(false)
    },
    setConfirmed,
    revealValidation: () => setAttempted(true),
    load,
    decide,
    retry: () => decide(pending.current?.decision ?? "accept", true),
    recheck,
  }
}
